import React from "react";

import cx from "classnames";

import {
  getClosureFor,
  composeSimpleFunctions,
  setState,
  setStateValue,
} from "@kazoohr/helpers";

import DatePicker from "../DatePicker";
import { Icon } from "../Icon";
import basePillStyles from "../Pill/Pill.scss";
import { Popover } from "../Popover";

import compoundPillStyles from "./CompoundPill.scss";

export interface CompoundDatePickerPillProps {
  /** Classname that will be applied to the pill */
  className?: string;
  /** Changes the style of the pill when true */
  lightBlueBackground?: boolean;
  /** Prop to manage open/close state of the popover */
  isPopoverOpen: boolean;
  /** This function will set the state of isPopoverOpen */
  setIsPopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** Classname that will be applied to the popover */
  popoverClassName?: string;
  /** Indicates that the element can be focused, and where it participates in sequential keyboard navigation */
  tabIndex?: number;
  /** It will render as a data-testid prop on the Pill component */
  "data-testid"?: string;
  /** Pill text */
  text: string;
  /** Closes when the user clicks away from the popover */
  closeOnClickAway?: boolean;
  /** Optionally attaches the calendar to the supplied element instead of <body> */
  attachTo?: HTMLElement;
  /** Make the input a compact input size. Default: false. */
  compact?: boolean;
  /** A default date or array of dates to display. Takes plain old JS Date objects. */
  defaultDate?: Date | Date[];
  /** Disable the input. Default: false */
  disabled?: boolean;
  /** Error message to be displayed, or `true` if the error styling should be applied without any message */
  error?: string;
  /** Grows the input to the full width of the container. Default: false */
  fullWidth?: boolean;
  /** The HTML id attribute to be assigned to the input. Defaults to `date-picker-n` */
  id?: string;
  /** Helper text for the control */
  helperText?: string;
  /** Label for the control */
  label?: string;
  /** Date object of the first selectable date */
  firstSelectableDate?: Date;
  /** Date object of the last selectable date */
  lastSelectableDate?: Date;
  /** String value of the calendar mode. Single date, multiple date, or date range. Default: `single` */
  mode?: "single" | "multiple" | "range";
  /** Function receiving the new date(s) when they are updated. Always sends an array as the first and only argument */
  onChange: (dates: Date[]) => void;
  /** Whether to show the calendar icon by the input. Default: true */
  showIcon?: boolean;
  /** A reference to the object that implements the calendar UX */
  flatpickrRef?: React.MutableRefObject<any>;
  /** A label that marks this date as optional */
  optionalLabelText?: string;
  /** Controls the format the date will be displayed in. See https://flatpickr.js.org/formatting/ */
  dateFormat?: string;
  /** Input tooltip */
  tooltip?: React.ReactNode;
  /** Whether to allow clearing the input. Default: false */
  allowClearing?: boolean;
  /** Placeholder text for the input, to be shown when the user has not selected or has cleared the dates */
  placeholder?: string;
  /** Allow keyboard input. Default true. */
  allowInput?: boolean;
  /** Value of the date picker */
  value?: Date[];
}

/**
 * A pill component that displays a popover when clicked
 */
// eslint-disable-next-line max-lines-per-function
export function CompoundDatePickerPill({
  "data-testid": testId,
  allowClearing = false,
  allowInput = true,
  attachTo,
  className,
  closeOnClickAway = true,
  compact = false,
  dateFormat = "M d, Y",
  defaultDate = new Date(),
  disabled = false,
  error = "",
  firstSelectableDate,
  flatpickrRef: flatpickrOutputRef,
  fullWidth = false,
  helperText = "",
  id,
  isPopoverOpen,
  label,
  lastSelectableDate,
  lightBlueBackground = false,
  mode = "single",
  onChange,
  optionalLabelText = "",
  placeholder,
  popoverClassName,
  setIsPopoverOpen,
  showIcon = true,
  tabIndex,
  text,
  tooltip,
  value,
}: CompoundDatePickerPillProps) {
  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);

  const onCalendarClose = composeSimpleFunctions(
    getClosureFor(setIsCalendarOpen, false),
    getClosureFor(setIsPopoverOpen, false)
  );

  const onCalendarOpen = getClosureFor(setIsCalendarOpen, true);

  const pillStyles = cx(
    basePillStyles["pill"],
    basePillStyles["pill--gray"],
    basePillStyles["large"],
    compoundPillStyles["compound-pill"],
    className,
    {
      [compoundPillStyles["compound-pill--active"]]: isPopoverOpen,
      [compoundPillStyles["compound-pill--light-blue-bg"]]: lightBlueBackground,
    }
  );

  const classNameForPopover = cx(
    compoundPillStyles["compound-pill--popover"],
    popoverClassName
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      setIsPopoverOpen(!isPopoverOpen);
    }
  };

  const pillComponent = (
    <div
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      role="button"
      data-testid={testId}
      onClick={setStateValue(setIsPopoverOpen, !isPopoverOpen)}
      className={pillStyles}
    >
      <div data-testid="pill-text">{text}</div>
      <div className={compoundPillStyles.icon}>
        <Icon icon="caret_down" size={7} variant="thin" color="currentColor" />
      </div>
    </div>
  );
  return (
    <Popover
      isOpen={isPopoverOpen}
      onSetOpen={setState(setIsPopoverOpen)}
      parent={pillComponent}
      className={classNameForPopover}
      position="bottom-start"
      data-testid="meatBallMenu-popover"
      closeOnClickAway={closeOnClickAway}
      ignoreCloseOnClickAway={
        closeOnClickAway && !(isPopoverOpen && !isCalendarOpen)
      }
      popoverComponent={
        <DatePicker
          allowInput={allowInput}
          attachTo={attachTo}
          compact={compact}
          data-testid={testId ? testId + "-datepicker" : "compound-date-picker"}
          dateFormat={dateFormat}
          defaultDate={defaultDate}
          disabled={disabled}
          error={error}
          firstSelectableDate={firstSelectableDate}
          flatpickrRef={flatpickrOutputRef}
          fullWidth={fullWidth}
          helperText={helperText}
          id={id}
          label={label}
          lastSelectableDate={lastSelectableDate}
          mode={mode}
          onChange={onChange}
          onClose={closeOnClickAway ? onCalendarClose : undefined}
          onOpen={onCalendarOpen}
          optionalLabelText={optionalLabelText}
          placeholder={placeholder}
          showClearButton={allowClearing}
          showIcon={showIcon}
          tooltip={tooltip}
          value={value}
        />
      }
    />
  );
}
